import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NzModalFooterDirective, NzModalRef } from 'ng-zorro-antd/modal';
import { DeliveryZonePolygon } from '@adm-web-app/modules/delivery-zones/city-map/city-map-property.component';
import { HxProductService, HxStoreService, HxUiLabelPipe, PriceShortModel, StoreFullModel, UiLabel } from 'hx-services';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { produce } from 'immer';
import { formatISO, isBefore, parseISO } from 'date-fns';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { HxStoreSelectComponent } from 'hx-components';
import { NgFor, NgIf } from '@angular/common';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';


interface OtherStore {
  storeId: number;
  priceId: number;
  priceValue: number;
  storeTitle: UiLabel;
}

@Component({
    selector: 'hc-polygon-price-edit',
    templateUrl: './polygon-price-edit.modal.html',
    styleUrls: ['./polygon-price-edit.modal.less'],
  imports: [TranslocoDirective, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxStoreSelectComponent, FormsModule, NgIf, NzSelectComponent, NgFor, NzOptionComponent, NzDividerComponent, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzTbodyComponent, NzFormDirective, ReactiveFormsModule, NzButtonComponent, NzWaveDirective, NzModalFooterDirective, HxUiLabelPipe, TranslocoPipe]
})
export class PolygonPriceEditModal implements OnInit, OnDestroy {

  @Input() date!: string;
  @Input() zone!: DeliveryZonePolygon;
  @Input() brandId!: number;
  @Input() cityId!: number;

  otherStores: OtherStore[] = [];
  prices: PriceShortModel[] = [];
  newPrices: PriceShortModel[] = [];
  otherStoreForm = new FormGroup({
    storeId: new FormControl<number | null>(null, [Validators.required]),
    priceId: new FormControl<number | null>(null, [Validators.required]),
  });
  cityStores: StoreFullModel[] = [];
  private $destroyed = new Subject<void>();

  constructor(
    public modalRef: NzModalRef,
    private productService: HxProductService,
    private storeService: HxStoreService,
  ) {
  }

  async ngOnInit() {
    this.otherStoreForm.controls.storeId.valueChanges.pipe(takeUntil(this.$destroyed)).subscribe(async newValue => {
      if (newValue) {
        this.newPrices = await this.productService.getDeliveryPrices({storeId: newValue, date: this.getDate()});
        if (!this.newPrices.some(price => price.id === this.otherStoreForm.controls.priceId.value)) {
          this.otherStoreForm.controls.priceId.setValue(null);
        }
      } else {
        this.newPrices = [];
      }
    });

    const stores = await this.storeService.getFullStores({cityId: this.cityId, limit: 1000, brandId: this.brandId});
    this.cityStores = stores.list;

    if (this.zone.otherStores.length > 0) {
      const prices = await Promise.all(this.zone.otherStores.map(os => this.productService.getPriceById(os.priceId)));
      const otherStores: OtherStore[] = [];
      this.zone.otherStores.forEach(os => {
        const price = prices.find(p => os.priceId === p.id);
        const store = this.cityStores.find(st => st.id === os.storeId);
        if (price && store) {
          otherStores.push({
            priceId: price.id,
            priceValue: price.value,
            storeId: store.id,
            storeTitle: store.title,
          });
        }
      });
      this.otherStores = otherStores;
    }
  }

  async onStoreIdChange() {
    const prices = await this.productService.getDeliveryPrices({storeId: this.zone.storeId, date: this.getDate()});
    this.prices = prices;
    if (!prices.some(price => price.id === this.zone.priceId)) {
      this.zone.priceId = undefined;
    }
  }

  deleteOtherStore(ix: number) {
    this.otherStores = produce(this.otherStores, draft => {
      draft.splice(ix, 1);
    });
  }

  addOtherStore() {
    const otherStore = this.otherStoreForm.value;
    const store = this.cityStores.find(store => store.id === otherStore.storeId);
    const price = this.newPrices.find(price => price.id === otherStore.priceId);
    if (store && price && store.id !== this.zone.storeId && !this.otherStores.some(os => os.storeId === store.id)) {
      this.otherStores = produce(this.otherStores, draft => {
        draft.push({
          priceId: price.id,
          priceValue: price.value,
          storeId: store.id,
          storeTitle: store.title,
        });
      });
      this.otherStoreForm.reset();
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  closeModal() {
    this.modalRef.close();
  }

  ok() {
    this.zone.otherStores = this.otherStores.map(os => ({priceId: os.priceId, storeId: os.storeId}));
    this.modalRef.close(this.zone);
  }

  private getDate() {
    return isBefore(parseISO(this.date), new Date()) ? formatISO(new Date(), {representation: 'date'}) : this.date;
  }
}
